import type { LabeledPreset, Preset } from '../types';
import {
  builtInPresetDefinitions,
  type BuiltInPresetId,
  type DateRangePresetTexts,
  type PresetDefinition,
} from './definitions';

type BuiltInPresets = Record<BuiltInPresetId, Preset>;

type LabeledBuiltInPresets = Record<BuiltInPresetId, LabeledPreset>;

function toRecord<T extends Preset>(presets: readonly T[]) {
  return Object.fromEntries(
    presets.map((preset) => [preset.id, preset])
  ) as Record<T['id'], T>;
}

function toPreset<Definition extends PresetDefinition>({
  id,
  createRange,
  maxDurationSeconds,
}: Definition): Preset & Pick<Definition, 'id'> {
  return {
    id,
    createRange,
    maxDurationSeconds,
  };
}

const createBuiltInPresets = () =>
  toRecord(builtInPresetDefinitions.map(toPreset));

export const builtInPresets: BuiltInPresets = createBuiltInPresets();

function toLocalizePreset<Definition extends PresetDefinition>(
  { id, getLabel, labelValues, createRange, maxDurationSeconds }: Definition,
  texts: DateRangePresetTexts
): LabeledPreset & Pick<Definition, 'id'> {
  return {
    id,
    createRange,
    maxDurationSeconds,
    label: getLabel(texts),
    ...(labelValues === undefined ? {} : { labelValues }),
  };
}

export function localize(texts: DateRangePresetTexts): LabeledBuiltInPresets {
  const presets = builtInPresetDefinitions.map((def) =>
    toLocalizePreset(def, texts)
  );
  return toRecord(presets);
}
